// src/pages/OrderDetailsPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";

const Container = styled.div`
    background: #f8f9fa;
    padding: 20px;
    min-height: 100vh;
`;

const Inner = styled.div`
    max-width: 1200px;
    margin: 0 auto;
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    background: none;
    border: none;
    color: #1b6392;
    font-size: 14px;
    cursor: pointer;
    margin-bottom: 24px;
    &:hover { text-decoration: underline; }
`;

const OrderSummary = styled.div`
    background: #fffbea;
    padding: 16px;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    border: 1px solid #f0e6b8;
`;

const OrderInfo = styled.div`
    font-size: 14px;
    color: #555;
    & b { color: #333; }
`;

const OrderTotal = styled.div`
    font-size: 20px;
    font-weight: 600;
    color: #1b6392;
`;

const StatusBadge = styled.span`
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    margin-left: 8px;
    background: ${props => {
        switch(props.status) {
            case 'WAITING_CONFIRMATION': return '#fff3cd';
            case 'CONFIRMED': return '#d1edff';
            case 'PAID': return '#d4edda';
            case 'PENDING': return '#f8d7da';
            case 'ACCEPTED': return '#d1ecf1';
            case 'DELIVERED': return '#d4edda';
            default: return '#e2e3e5';
        }
    }};
    color: ${props => {
        switch(props.status) {
            case 'WAITING_CONFIRMATION': return '#856404';
            case 'CONFIRMED': return '#004085';
            case 'PAID': return '#155724';
            case 'PENDING': return '#721c24';
            case 'ACCEPTED': return '#0c5460';
            case 'DELIVERED': return '#155724';
            default: return '#383d41';
        }
    }};
`;

const Timeline = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 32px;
`;

const Step = styled.div`
    flex: 1;
    text-align: center;
    color: ${p => (p.active ? "#1b6392" : "#ccc")};
`;

const StepLine = styled.div`
    height: 2px;
    flex: 1;
    margin: 0 8px;
    background: ${p => (p.active ? "#1b6392" : "#eee")};
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 32px;
    @media (max-width: 800px) {
        grid-template-columns: 1fr;
    }
`;

const FullWidthCard = styled.div`
    background: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    margin-bottom: 24px;
`;

const InfoCard = styled.div`
    background: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
`;

const CardTitle = styled.h4`
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #333;
`;

const Field = styled.div`
    margin-bottom: 8px;
    font-size: 13px;
    color: #555;
    & strong { color: #333; }
`;

const ItemsTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-top: 16px;
`;

const TableHeader = styled.th`
    background: #f8f9fa;
    padding: 12px;
    text-align: left;
    font-size: 14px;
    font-weight: 600;
    color: #333;
    border-bottom: 2px solid #dee2e6;
`;

const TableCell = styled.td`
    padding: 12px;
    font-size: 14px;
    color: #555;
    border-bottom: 1px solid #dee2e6;
    &.number { text-align: right; }
    &.name { font-weight: 500; color: #333; }
`;

const FileLink = styled.a`
    color: #1b6392;
    text-decoration: none;
    &:hover { text-decoration: underline; }
`;

export default function OrderDetailsPage() {
    const { orderId } = useParams();
    const navigate     = useNavigate();
    const [order, setOrder] = useState(null);

    useEffect(() => {
        const token = sessionStorage.getItem("accessToken");
        if (!token) {
            navigate("/profile/orders", { replace: true });
            return;
        }

        axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/user/user-order/${orderId}`,
            { headers: { Authorization: `Bearer ${token}` } }
        )
            .then(({ data }) => setOrder(data))
            .catch(() => navigate("/profile/orders", { replace: true }));
    }, [orderId, navigate]);

    if (!order) {
        return <Container>Загрузка данных заказа…</Container>;
    }

    // timeline steps based on trackStatus
    const steps = [
        { key: "PENDING",   label: "Ожидание" },
        { key: "ACCEPTED",  label: "Принят" },
        { key: "DELIVERED", label: "Доставлено" },
    ];
    const currentIndex = steps.findIndex(s => s.key === order.trackStatus);

    const getStatusLabel = (status) => {
        const statusLabels = {
            'WAITING_CONFIRMATION': 'Ожидает подтверждения',
            'CONFIRMED': 'Подтверждён',
            'PAID': 'Оплачен',
            'PENDING': 'Ожидание',
            'ACCEPTED': 'Принят',
            'DELIVERED': 'Доставлено'
        };
        return statusLabels[status] || status;
    };

    return (
        <Container>
            <Inner>
                <BackButton onClick={() => navigate(-1)}>
                    ← Назад
                </BackButton>

                <OrderSummary>
                    <OrderInfo>
                        <div><b>Заказ #{order.orderId}</b></div>
                        <div>
                            {order.items?.length || 0} товар(ов) ·{" "}
                            {new Date(order.orderDate).toLocaleString("ru")}
                        </div>
                        <div>
                        </div>
                        <div>
                        </div>
                    </OrderInfo>
                    <OrderTotal>{order.totalPrice?.toLocaleString('ru')} ₸</OrderTotal>
                </OrderSummary>

                <Timeline>
                    {steps.map((step, i) => (
                        <React.Fragment key={step.key}>
                            <Step active={i <= currentIndex}>{step.label}</Step>
                            {i < steps.length - 1 && (
                                <StepLine active={i < currentIndex} />
                            )}
                        </React.Fragment>
                    ))}
                </Timeline>

                {/* Товары в заказе */}
                <FullWidthCard>
                    <CardTitle>Товары в заказе</CardTitle>
                    <ItemsTable>
                        <thead>
                        <tr>
                            <TableHeader>Название товара</TableHeader>
                            <TableHeader>Количество</TableHeader>
                            <TableHeader>Цена за единицу</TableHeader>
                            <TableHeader>Сумма</TableHeader>
                        </tr>
                        </thead>
                        <tbody>
                        {order.items?.map((item, index) => (
                            <tr key={index}>
                                <TableCell className="name">{item.name}</TableCell>
                                <TableCell className="number">{item.quantity}</TableCell>
                                <TableCell className="number">{item.price?.toLocaleString('ru')} ₸</TableCell>
                                <TableCell className="number">
                                    {(item.quantity * item.price)?.toLocaleString('ru')} ₸
                                </TableCell>
                            </tr>
                        ))}
                        </tbody>
                    </ItemsTable>
                </FullWidthCard>

                <Grid>
                    <InfoCard>
                        <CardTitle>Информация о покупателе</CardTitle>
                        <Field><strong>Имя:</strong> {order.firstname || 'Не указано'}</Field>
                        <Field><strong>Фамилия:</strong> {order.lastname || 'Не указано'}</Field>
                        <Field><strong>Телефон:</strong> {order.phoneNumber || 'Не указан'}</Field>
                        <Field><strong>Username:</strong> {order.username}</Field>
                        <Field><strong>Email:</strong> {order.email || 'Не указан'}</Field>
                    </InfoCard>

                    <InfoCard>
                        <CardTitle>Адрес доставки</CardTitle>
                        <Field><strong>Страна:</strong> {order.country}</Field>
                        <Field><strong>Город:</strong> {order.city || 'Не указан'}</Field>
                        <Field><strong>Пункт выдачи:</strong> {order.point}</Field>
                    </InfoCard>
                </Grid>

                {/* Дополнительная информация */}
                <Grid>
                    <InfoCard>
                        <CardTitle>Статусы заказа</CardTitle>
                        <Field>
                            <strong>Статус оплаты:</strong>
                            <StatusBadge status={order.payStatus}>
                                {getStatusLabel(order.payStatus)}
                            </StatusBadge>
                        </Field>
                        <Field>
                            <strong>Статус доставки:</strong>
                            <StatusBadge status={order.trackStatus}>
                                {getStatusLabel(order.trackStatus)}
                            </StatusBadge>
                        </Field>
                        <Field><strong>Дата заказа:</strong> {new Date(order.orderDate).toLocaleString("ru")}</Field>
                    </InfoCard>

                    <InfoCard>
                        <CardTitle>Документы</CardTitle>
                        {order.fileName ? (
                            <Field>
                                <strong>Прикреплённый файл:</strong><br/>
                                <FileLink
                                    href={`${process.env.REACT_APP_API_BASE_URL}/files/${order.fileName}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {order.fileName.split('_').slice(1).join('_') || order.fileName}
                                </FileLink>
                            </Field>
                        ) : (
                            <Field>Файлы не прикреплены</Field>
                        )}
                    </InfoCard>
                </Grid>
            </Inner>
        </Container>
    );
}
